import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  DEFAULT_SPEED_OPTIONS,
  FULL_PLAYER_MAX_WIDTH_TABLET,
  FULL_PLAYER_TEXT_LABEL_GUTTER,
  MINI_PROGRESS_WIDTH_DESKTOP,
  MINI_SIDE_CONTAINER_WIDTH_DESKTOP,
} from '../audio-player-constants';
import {
  getAudioImageSrc,
  getBoolean,
  getTypographyStyle,
  resolveEntryText,
} from '../audio-player-utils';
import type { FeedEntry } from '~/services/layout';
import type { IconAssets } from '../types';
import type { CSSProperties } from 'react';

type UseAudioPlayerDerivedParams = {
  audioPlayerConfig: Record<string, unknown>;
  queueWebConfig: Record<string, unknown>;
  entry: FeedEntry | null;
  currentTime: number;
  duration: number;
};

type QueuePanelLabels = {
  nowPlayingLabel: string;
  nextUpTitle: string;
  nextUpEmptyLabel: string;
  clearQueueButtonLabel: string;
};

type QueuePanelAssets = {
  mobileNowPlayingPlayAsset?: string;
  mobileNowPlayingPauseAsset?: string;
  desktopNowPlayingPlayAsset?: string;
  desktopNowPlayingPauseAsset?: string;
  mobileQueueRemoveAsset?: string;
  desktopQueueRemoveAsset?: string;
  mobileQueueReorderAsset?: string;
  desktopQueueReorderAsset?: string;
};

type QueuePanelItemConfig = {
  mobileImageDataKey?: string;
  desktopImageDataKey?: string;
  subtitleDataKey?: string;
};

type QueuePanelNowPlayingConfig = {
  mobileImageDataKey?: string;
  desktopImageDataKey?: string;
  mobileImagePlaceholderColor?: string;
  desktopImagePlaceholderColor?: string;
  mobileLabelEnabled: boolean;
  desktopLabelEnabled: boolean;
  mobileSubtitleEnabled: boolean;
  desktopSubtitleEnabled: boolean;
  mobileEqualizerAsset?: string;
  desktopEqualizerAsset?: string;
};

type QueuePanelTextTransforms = {
  mobilePanelTitle?: string;
  desktopPanelTitle?: string;
  mobileHeaderTitle?: string;
  mobileHeaderClearButton?: string;
  desktopHeaderTitle?: string;
  desktopHeaderClearButton?: string;
};

type UseAudioPlayerDerivedResult = {
  general: Record<string, any>;
  styles: Record<string, any>;
  localizations: Record<string, any>;
  speedOptions: number[];
  hasSpeedControl: boolean;
  hasQueue: boolean;
  hasAddToPlaylistAction: boolean;
  showMiniFavoriteDesktop: boolean;
  showMiniAddDesktop: boolean;
  showFullFavoriteMobile: boolean;
  miniTitle: string;
  miniArtist: string;
  fullTitle: string;
  fullArtist: string;
  fullPlaylistName: string;
  miniImageSrc?: string;
  fullImageSrc?: string;
  miniImageCornerRadius: string;
  miniImagePlaceholderColor: string;
  miniImageBorderColor: string;
  miniProgressWidth: string;
  miniSideContainerWidth: string;
  miniTitleColor: string;
  miniArtistColor: string;
  barProgressColor: string;
  barTotalColor: string;
  barHandleColor: string;
  elapsedLabelColor: string;
  totalLabelColor: string;
  miniTitleTextStyle: CSSProperties;
  miniArtistTextStyle: CSSProperties;
  fullTitleTextStyle: CSSProperties;
  fullArtistTextStyle: CSSProperties;
  fullPlaylistTextStyle: CSSProperties;
  timeLabelTextStyle: CSSProperties;
  playbackButtonInteractionStyle: CSSProperties;
  iconAssets: IconAssets;
  queueTitle: string;
  queuePanelLabels: QueuePanelLabels;
  queuePanelAssets: QueuePanelAssets;
  queuePanelItemConfig: QueuePanelItemConfig;
  queuePanelNowPlayingConfig: QueuePanelNowPlayingConfig;
  queuePanelTextTransforms: QueuePanelTextTransforms;
  fullImageCornerRadius: string;
  fullImagePlaceholderColor: string;
  fullImageBorderColor: string;
  fullContainerClassName: string;
  miniContainerClassName: string;
  remaining: number;
  fullMaxWidthTablet: string;
  fullTextGutter: string;
};

export function hasActionAlias({
  entry,
  alias,
}: {
  entry: FeedEntry | null;
  alias: string;
}): boolean {
  const normalizedAlias = alias.trim().toLowerCase();

  if (!normalizedAlias) {
    return false;
  }

  const entryActions =
    entry && typeof entry === 'object'
      ? (
          entry as FeedEntry & {
            extensions?: {
              entry_action?: Array<{
                alias?: string;
                action_alias?: string;
                button?: { alias?: string };
              }>;
            };
          }
        ).extensions?.entry_action
      : undefined;

  if (!Array.isArray(entryActions)) {
    return false;
  }

  return entryActions.some((entryAction) => {
    const aliasCandidates = [
      entryAction?.button?.alias,
      entryAction?.alias,
      entryAction?.action_alias,
    ];

    return aliasCandidates.some((candidate) => {
      if (typeof candidate !== 'string') {
        return false;
      }

      return candidate.trim().toLowerCase() === normalizedAlias;
    });
  });
}

/** Computes view-model data and configuration-derived values for player rendering. */
export function useAudioPlayerDerived({
  audioPlayerConfig,
  queueWebConfig,
  entry,
  currentTime,
  duration,
}: UseAudioPlayerDerivedParams): UseAudioPlayerDerivedResult {
  const general = audioPlayerConfig.general as Record<string, any>;
  const styles = audioPlayerConfig.styles as Record<string, any>;
  const localizations = audioPlayerConfig.localizations as Record<string, any>;
  const queueLocalizations = queueWebConfig.localizations as Record<
    string,
    any
  >;
  const queueCustomConfiguration = queueWebConfig.customConfiguration as Record<
    string,
    any
  >;

  const speedOptions = DEFAULT_SPEED_OPTIONS;
  const hasSpeedControl = Boolean(general.enable_playback_speed_options);
  const hasQueue = Boolean(general.enable_queue);
  const hasAddToPlaylistAction = useMemo(() => {
    return hasActionAlias({
      entry,
      alias: 'add_to_playlist',
    });
  }, [entry]);
  const showMiniFavoriteDesktop = Boolean(
    general.enable_favoriting_on_mini_player
  );
  const showMiniAddDesktop =
    Boolean(general.enable_add_to_playlist_on_mini_player) &&
    hasAddToPlaylistAction;
  const showFullFavoriteMobile = Boolean(
    general.enable_favoriting_on_full_player_mobile
  );

  const miniTitle = useMemo(
    () => resolveEntryText(entry, styles.mini_player_title_data_key),
    [entry, styles.mini_player_title_data_key]
  );
  const miniArtist = useMemo(
    () => resolveEntryText(entry, styles.mini_player_artist_data_key),
    [entry, styles.mini_player_artist_data_key]
  );
  const fullTitle = useMemo(
    () => resolveEntryText(entry, styles.full_player_title_data_key),
    [entry, styles.full_player_title_data_key]
  );
  const fullArtist = useMemo(
    () => resolveEntryText(entry, styles.full_player_artist_data_key),
    [entry, styles.full_player_artist_data_key]
  );
  const fullPlaylistName = useMemo(
    () =>
      resolveEntryText(entry, styles.full_player_playlist_name_data_key, ''),
    [entry, styles.full_player_playlist_name_data_key]
  );

  const miniImageSrc = useMemo(
    () => getAudioImageSrc(entry, styles.mini_player_image_key),
    [entry, styles.mini_player_image_key]
  );
  const fullImageSrc = useMemo(
    () => getAudioImageSrc(entry, styles.full_player_image_key),
    [entry, styles.full_player_image_key]
  );

  const miniImageCornerRadius =
    'var(--mini_player_image_corner_radius_resolved, 12px)';
  const miniImagePlaceholderColor = styles.mini_player_image_placeholder_color;
  const miniImageBorderColor =
    'var(--mini_player_image_border_color_resolved, rgba(239, 239, 239, 0.1))';
  const miniBlurMobileTabletEnabled = getBoolean(
    styles.mini_player_background_blur_mobile_tablet,
    false
  );
  const miniBlurDesktopLargeDesktopEnabled = getBoolean(
    styles.mini_player_background_blur_desktop_large_desktop,
    false
  );
  const miniProgressWidth = MINI_PROGRESS_WIDTH_DESKTOP;
  const miniSideContainerWidth = MINI_SIDE_CONTAINER_WIDTH_DESKTOP;

  const miniTitleColor =
    'var(--mini_player_title_font_color_resolved, rgba(239, 239, 239, 1))';
  const miniArtistColor =
    'var(--mini_player_artist_font_color_resolved, rgba(239, 239, 239, 0.65))';

  const barProgressColor =
    'var(--bar_progress_color_resolved, rgba(239, 239, 239, 1))';
  const barTotalColor =
    'var(--bar_total_color_resolved, rgba(239, 239, 239, 0.15))';
  const barHandleColor =
    'var(--bar_handle_color_resolved, rgba(239, 239, 239, 1))';
  const elapsedLabelColor =
    'var(--elapsed_label_color_resolved, rgba(239, 239, 239, 1))';
  const totalLabelColor =
    'var(--total_label_color_resolved, rgba(239, 239, 239, 0.65))';

  const miniTitleTextStyle = getTypographyStyle('mini_player_title');
  const miniArtistTextStyle = getTypographyStyle('mini_player_artist');
  const fullTitleTextStyle = getTypographyStyle('full_player_title');
  const fullArtistTextStyle = getTypographyStyle('full_player_artist');
  const fullPlaylistTextStyle = getTypographyStyle('full_player_playlist_name');
  const timeLabelTextStyle = getTypographyStyle('time_label');

  const playbackButtonScaleDefault = 0.9;
  const playbackButtonScaleHover = 1.0;
  const playbackButtonScalePressed = 0.9;
  const playbackButtonSpringDuration = 0.664;
  const playbackButtonSpringEasing =
    'linear(0, 0.0043 0.91%, 0.0246, 0.0584, 0.1023 4.99%, 0.1903 7.26%, 0.5401 15.42%, 0.6412, 0.7285, 0.8014, 0.8604 26.31%, 0.9135, 0.9521, 0.9788, 0.9964 39.01%, 1.0081 42.64%, 1.014 46.72%, 1.0144 53.53%, 1.0054 68.5%, 1.0018 77.57%, 0.9998 99.8%)';

  const playbackButtonInteractionStyle = {
    '--playback-button-scale-default': `${playbackButtonScaleDefault}`,
    '--playback-button-scale-hover': `${playbackButtonScaleHover}`,
    '--playback-button-scale-pressed': `${playbackButtonScalePressed}`,
    '--playback-button-spring-duration': `${playbackButtonSpringDuration}s`,
    '--playback-button-spring-easing': playbackButtonSpringEasing,
  } as CSSProperties;

  const iconAssets = {
    play: styles.play_asset,
    pause: styles.pause_asset,
    stop: styles.stop_asset,
    previous: styles.previous_asset,
    next: styles.next_asset,
    skipAhead: styles.skip_ahead_asset,
    skipBack: styles.skip_backwards_asset,
    speed: styles.speed_asset,
    queue: styles.queue_asset,
    volume: styles.volume_asset,
    muted: styles.muted_asset,
    favorite: styles.favorite_asset,
    favorited: styles.favorited_asset,
    addToPlaylist: styles.add_to_playlist_asset,
    addedToPlaylist: styles.playlist_checkbox_added_asset,
    close: styles.close_asset,
    minimize: styles.minimize_asset,
    share: styles.share_asset,
    more: styles.more_asset,
  } as IconAssets;

  const queueTitle = queueLocalizations.queue_title;

  const queuePanelLabels: QueuePanelLabels = {
    nowPlayingLabel: queueLocalizations.now_playing_label,
    nextUpTitle: queueLocalizations.next_up_section_title,
    nextUpEmptyLabel: queueLocalizations.next_up_empty_message,
    clearQueueButtonLabel: queueLocalizations.clear_queue_button_label,
  };

  const queuePanelAssets: QueuePanelAssets = {
    mobileNowPlayingPlayAsset:
      queueCustomConfiguration.mobile_now_playing_play_asset,
    mobileNowPlayingPauseAsset:
      queueCustomConfiguration.mobile_now_playing_pause_asset,
    desktopNowPlayingPlayAsset:
      queueCustomConfiguration.desktop_now_playing_play_asset,
    desktopNowPlayingPauseAsset:
      queueCustomConfiguration.desktop_now_playing_pause_asset,
    mobileQueueRemoveAsset:
      queueCustomConfiguration.mobile_queue_item_remove_from_queue_asset,
    desktopQueueRemoveAsset:
      queueCustomConfiguration.desktop_queue_item_remove_from_queue_asset,
    mobileQueueReorderAsset:
      queueCustomConfiguration.mobile_queue_item_reorder_handle_asset,
    desktopQueueReorderAsset:
      queueCustomConfiguration.desktop_queue_item_reorder_handle_asset,
  };

  const queuePanelItemConfig: QueuePanelItemConfig = {
    mobileImageDataKey:
      queueCustomConfiguration.mobile_queue_item_image_data_key,
    desktopImageDataKey:
      queueCustomConfiguration.desktop_queue_item_image_data_key,
    subtitleDataKey:
      typeof styles.full_player_artist_data_key === 'string'
        ? styles.full_player_artist_data_key
        : undefined,
  };

  const queuePanelNowPlayingConfig: QueuePanelNowPlayingConfig = {
    mobileImageDataKey:
      queueCustomConfiguration.mobile_now_playing_image_data_key,
    desktopImageDataKey:
      queueCustomConfiguration.desktop_now_playing_image_data_key,
    mobileImagePlaceholderColor:
      queueCustomConfiguration.mobile_now_playing_image_placeholder_color,
    desktopImagePlaceholderColor:
      queueCustomConfiguration.desktop_now_playing_image_placeholder_color,
    mobileLabelEnabled: getBoolean(
      queueCustomConfiguration.mobile_now_playing_now_playing_label_enabled,
      true
    ),
    desktopLabelEnabled: getBoolean(
      queueCustomConfiguration.desktop_now_playing_now_playing_label_enabled,
      true
    ),
    mobileSubtitleEnabled: getBoolean(
      queueCustomConfiguration.mobile_now_playing_subtitle_enabled,
      true
    ),
    desktopSubtitleEnabled: getBoolean(
      queueCustomConfiguration.desktop_now_playing_subtitle_enabled,
      true
    ),
    mobileEqualizerAsset:
      queueCustomConfiguration.mobile_now_playing_equalizer_animation_asset,
    desktopEqualizerAsset:
      queueCustomConfiguration.desktop_now_playing_equalizer_animation_asset,
  };

  const queuePanelTextTransforms: QueuePanelTextTransforms = {
    mobilePanelTitle:
      queueCustomConfiguration.mobile_queue_panel_title_text_transform,
    desktopPanelTitle:
      queueCustomConfiguration.desktop_queue_panel_title_text_transform,
    mobileHeaderTitle:
      queueCustomConfiguration.mobile_queue_header_title_text_transform,
    mobileHeaderClearButton:
      queueCustomConfiguration.mobile_queue_header_clear_button_text_transform,
    desktopHeaderTitle:
      queueCustomConfiguration.desktop_queue_header_title_text_transform,
    desktopHeaderClearButton:
      queueCustomConfiguration.desktop_queue_header_clear_button_text_transform,
  };

  const fullImageCornerRadius =
    'var(--full_player_image_corner_radius_resolved, 24px)';
  const fullImagePlaceholderColor = styles.full_player_image_placeholder_color;
  const fullImageBorderColor =
    'var(--full_player_image_border_color_resolved, rgba(239, 239, 239, 0.1))';
  const fullPlayerBackgroundBlurMobileTablet = getBoolean(
    styles.full_player_background_blur_mobile_tablet,
    false
  );
  const fullPlayerBackgroundBlurDesktopLargeDesktop = getBoolean(
    styles.full_player_background_blur_desktop_large_desktop,
    false
  );

  const miniContainerClassName = twMerge(
    'audio-player-mini_container_styles',
    miniBlurMobileTabletEnabled &&
      'audio-player-mini_container_blur_mobile_tablet',
    miniBlurDesktopLargeDesktopEnabled &&
      'audio-player-mini_container_blur_desktop_large_desktop'
  );
  const fullContainerClassName = twMerge(
    'audio-player-full_container_styles',
    fullPlayerBackgroundBlurMobileTablet &&
      'audio-player-full_container_blur_mobile_tablet',
    fullPlayerBackgroundBlurDesktopLargeDesktop &&
      'audio-player-full_container_blur_desktop_large_desktop'
  );

  const remaining = Math.max(duration - currentTime, 0);

  return {
    general,
    styles,
    localizations,
    speedOptions,
    hasSpeedControl,
    hasQueue,
    hasAddToPlaylistAction,
    showMiniFavoriteDesktop,
    showMiniAddDesktop,
    showFullFavoriteMobile,
    miniTitle,
    miniArtist,
    fullTitle,
    fullArtist,
    fullPlaylistName,
    miniImageSrc,
    fullImageSrc,
    miniImageCornerRadius,
    miniImagePlaceholderColor,
    miniImageBorderColor,
    miniProgressWidth,
    miniSideContainerWidth,
    miniTitleColor,
    miniArtistColor,
    barProgressColor,
    barTotalColor,
    barHandleColor,
    elapsedLabelColor,
    totalLabelColor,
    miniTitleTextStyle,
    miniArtistTextStyle,
    fullTitleTextStyle,
    fullArtistTextStyle,
    fullPlaylistTextStyle,
    timeLabelTextStyle,
    playbackButtonInteractionStyle,
    iconAssets,
    queueTitle,
    queuePanelLabels,
    queuePanelAssets,
    queuePanelItemConfig,
    queuePanelNowPlayingConfig,
    queuePanelTextTransforms,
    fullImageCornerRadius,
    fullImagePlaceholderColor,
    fullImageBorderColor,
    fullContainerClassName,
    miniContainerClassName,
    remaining,
    fullMaxWidthTablet: FULL_PLAYER_MAX_WIDTH_TABLET,
    fullTextGutter: FULL_PLAYER_TEXT_LABEL_GUTTER,
  };
}
